import { DateTime } from "luxon";

// A time group fans out across trainers, locations, or both. The chip always
// labels availability rather than naming a trainer: the trainer is never the
// thing being chosen on the chip (that happens in the picker), and when the
// service assigns them the name must not leak at all. `showLocation` is on only
// for multi-branch clubs, where naming the single branch behind a group is
// useful rather than noise. `showDuration` (the "All durations" filter) leads
// with the lengths on offer at this time — "45–90 min · 2 trainers".
export function getOptionsLabel(slots, showLocation = false, showDuration = false) {
  const trainers = new Set(slots.map((s) => s.trainerId).filter(Boolean));
  const locations = new Set(slots.map((s) => s.locationId).filter(Boolean));
  const prefix = showDuration ? `${durationRangeLabel(slots)} · ` : "";

  if (locations.size > 1) return `${prefix}${locations.size} locations`;

  const count = trainers.size || slots.length;
  const label = `${prefix}${count} ${count === 1 ? "trainer" : "trainers"}`;
  const locationName = slots[0]?.locationName;
  return showLocation && locationName ? `${label} · ${locationName}` : label;
}

// "60 min" for one length, "45–90 min" for several.
export function durationRangeLabel(slots) {
  const ds = [...new Set(slots.map((s) => parseInt(s.duration, 10)).filter(Number.isInteger))].sort((a, b) => a - b);
  if (!ds.length) return "";
  return ds.length === 1 ? `${ds[0]} min` : `${ds[0]}–${ds[ds.length - 1]} min`;
}

// One entry per location, each keeping its own slots. Used when the trainer is
// assigned but several branches offer the time — the branch is still a choice.
export function groupSlotsByLocation(slots) {
  const byLocation = new Map();
  for (const slot of slots || []) {
    if (!byLocation.has(slot.locationId)) {
      byLocation.set(slot.locationId, { locationId: slot.locationId, locationName: slot.locationName, slots: [] });
    }
    byLocation.get(slot.locationId).slots.push(slot);
  }
  return Array.from(byLocation.values());
}

// Collapse a time group's slots into one row per trainer. Under "All Locations"
// a trainer can appear once per location; this keeps a single row and exposes
// the distinct locations so the picker can prompt for one on select. Returns
// [{ trainerId, trainerName, slots, locations }] where `locations` is the set of
// { locationId, locationName, slot } the trainer is available at.
export function dedupeSlotsByTrainer(slots) {
  const byTrainer = new Map();
  for (const slot of slots || []) {
    const key = slot.trainerId || slot.pricingId;
    if (!byTrainer.has(key)) {
      byTrainer.set(key, { trainerId: slot.trainerId, trainerName: slot.trainerName, slots: [] });
    }
    byTrainer.get(key).slots.push(slot);
  }
  return Array.from(byTrainer.values()).map((g) => {
    const locMap = new Map();
    for (const s of g.slots) {
      if (!locMap.has(s.locationId)) locMap.set(s.locationId, { locationId: s.locationId, locationName: s.locationName, slot: s });
    }
    return { ...g, locations: Array.from(locMap.values()) };
  });
}

const wallDay = (iso) => (iso ? DateTime.fromISO(iso, { zone: "utc" }).toISODate() : null);
export const dayLabel = (isoDay) => DateTime.fromISO(isoDay).toFormat("ccc d LLL");

// The days a lesson moved with a package can land on: the pack only pays between its own
// start and end. Null for a money move, and for an open end.
export function packWindow(info) {
  if (info?.mode !== "PACKAGE_MOVE" || !info.pack) return null;
  return { start: wallDay(info.pack.startDate), end: info.pack.noExpiry ? null : wallDay(info.pack.expiryDate) };
}

// A day (or week, `from`–`to`) outside the package's own dates: that is the reason nothing
// can be moved there, and the way out is its first (or last) day. Null inside them.
export function packWindowEmptyProps(info, { from, to = from, week = false, onJumpTo }) {
  const pack = packWindow(info);
  const packName = info?.pack?.name ? `"${info.pack.name}" ` : "";
  const range = pack?.start ? `${dayLabel(pack.start)}${pack.end ? ` to ${dayLabel(pack.end)}` : ""}` : "";

  if (pack?.start && to < pack.start) {
    return {
      title: `Your package starts on ${dayLabel(pack.start)}`,
      description: `Your ${packName}package pays for lessons from ${range}, so your lesson can't move ${week ? "into this week" : "to this day"}.`,
      actionLabel: `Go to ${dayLabel(pack.start)}`,
      onAction: () => onJumpTo(pack.start)
    };
  }
  if (pack?.end && from > pack.end) {
    return {
      title: `Your package runs until ${dayLabel(pack.end)}`,
      description: `Your ${packName}package pays for lessons up to ${dayLabel(pack.end)}, so your lesson can't move ${week ? "into this week" : "to this day"}.`,
      actionLabel: `Go to ${dayLabel(pack.end)}`,
      onAction: () => onJumpTo(pack.end)
    };
  }
  return null;
}

// Nothing to move to, with no more specific reason (coach off, the service not running
// that day). The court under a lesson is picked for the client, so the copy is about coaches. A money-paid lesson only moves to times at the price that was
// paid, and the booking notice still applies — say so, so an empty day doesn't read as
// "the club is full".
export function rescheduleEmptyProps(info, { week = false, onNext }) {
  const when = week ? "this week" : "on this day";
  const rule =
    info?.mode === "MONEY_MOVE"
      ? "Your lesson can move to any time a coach is free, at the same price you paid."
      : "Your lesson can move to any time a coach is free that your package covers.";
  return {
    title: `No times to move to ${when}`,
    description: `${rule} Try another ${week ? "week" : "day"} or coach.`,
    actionLabel: week ? "Next week" : "Try tomorrow",
    onAction: onNext
  };
}

